<script setup lang="ts">
  import { IconAdmin, IconCalendarClock, IconUpload } from '@gat-ship/icons'
  import type { AddTenantUserRequest, ISODateTimeString } from '@gat-ship/types'
  import { testIds } from '@gat-ship/types'
  import { globalUtils, useCommandId, useFormatting } from '@gat-ship/utils'
  import {
    ActionCard,
    ActionCardDescription,
    ActionCardLeadIcon,
    ActionCardTitle,
    Button,
    FormField,
    ModalSectionHeader,
    ModalSectionHeaderTitle,
    FormSubmit,
    InputGroupInput,
    InputGroupPhone,
    Switch,
    isValidPhoneNumber,
    DateCalendar,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderTitle,
    ModalSection,
    Popover,
    PopoverContent,
    PopoverTrigger,
    useAppLoading,
    useForm,
    z,
    Avatar,
    AvatarImage,
    AvatarFallback
  } from '@gat-ship/vue-components'
  import { FieldGroup } from '@gat-ship/vue-components'
  import { computed, ref, useTemplateRef } from 'vue'
  import { Temporal } from '@js-temporal/polyfill'
  import { useTenantUserApiCalls } from '../../composables/useTenantUserApiCalls'
  import { useUser } from '@gat-ship/user'
  import { useProfileImageUpload } from '../../composables/useProfileImageUpload'
  import { useTenantUsersCollectionContext } from '../../composables/useTenantUsersCollection'

  const fileInputRef = useTemplateRef('fileInput')
  const isOpen = ref(false)
  const tenantUsersProvider = useTenantUsersCollectionContext()!
  const { checkTenantUserEmailAvailabilityRequest } = useTenantUserApiCalls()
  const { newCommandId } = useCommandId()
  const { formatDate } = useFormatting()
  const user = useUser()
  const { uploadProfileImage } = useProfileImageUpload()
  const { setLoadingId, removeLoadingId, isLoading } = useAppLoading()

  let userId = globalUtils.functions.uuid()
  const previewUrl = ref<string | undefined>(undefined)

  const addTenantUserSchema = z.object({
    fullName: z.string().min(1, 'Full Name is required'),
    initials: z.string().min(1, 'Required').max(3, 'Max 3 characters'),
    email: z.string().min(1, 'Email is required'),
    title: z.string().optional(),
    mobilePhoneNumber: z.string().optional(),
    activeFrom: z.string().optional(),
    grantTenantAdminPrivileges: z.boolean(),
    processedImageUploadId: z.string().optional()
  })
  type AddTenantUserFormValues = z.infer<typeof addTenantUserSchema>

  const defaultValues: AddTenantUserFormValues = {
    fullName: '',
    initials: '',
    email: '',
    title: '',
    mobilePhoneNumber: undefined,
    activeFrom: undefined,
    grantTenantAdminPrivileges: false,
    processedImageUploadId: undefined
  }

  const form = useForm({
    defaultValues,
    validators: {
      onSubmit: addTenantUserSchema
    },
    onSubmit: async ({ value }) => {
      const payload: AddTenantUserRequest = {
        userId,
        email: value.email,
        fullName: value.fullName,
        initials: value.initials,
        title: value.title || undefined,
        mobilePhoneNumber: value.mobilePhoneNumber || undefined,
        activeFrom: value.activeFrom
          ? (value.activeFrom as ISODateTimeString)
          : undefined,
        grantTenantAdminPrivileges: value.grantTenantAdminPrivileges,
        sendWelcomeEmail: false,
        processedImageUploadId: value.processedImageUploadId
      }

      await tenantUsersProvider.commands.addTenantUser(
        payload,
        previewUrl.value
      )
      isOpen.value = false
    }
  })

  const phoneValidators = {
    onBlur: ({ value }: { value: string }) =>
      !value || value.length === 0
        ? undefined
        : isValidPhoneNumber(value)
          ? undefined
          : 'Invalid phone number'
  }

  const activeFromValue = form.useStore((s) => s.values.activeFrom)
  const activationPopoverOpen = ref(false)
  const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

  const activationDescription = computed(() => {
    const v = activeFromValue.value
    if (!v) return 'Optional activation date'
    return `Will activate on ${formatDate(v as ISODateTimeString, 'long')}`
  })

  const calendarValue = computed<Temporal.PlainDate | null>(() => {
    const v = activeFromValue.value
    if (!v) return null
    return Temporal.Instant.from(v)
      .toZonedDateTimeISO(localTimeZone)
      .toPlainDate()
  })

  function onActivationDateSelected(value: unknown) {
    // DateCalendar in single mode emits PlainDate | null; DateRange is
    // only emitted in range mode, which we don't use here.
    if (!value || typeof value !== 'object' || 'from' in value) return
    const date = value as Temporal.PlainDate
    const iso = date
      .toZonedDateTime({
        timeZone: localTimeZone,
        plainTime: Temporal.PlainTime.from('00:00:00')
      })
      .toInstant()
      .toString() as ISODateTimeString
    form.setFieldValue('activeFrom', iso)
    activationPopoverOpen.value = false
  }

  function clearActivationDate() {
    form.setFieldValue('activeFrom', undefined)
    activationPopoverOpen.value = false
  }

  function onUploadPhotoClicked() {
    fileInputRef.value?.click()
  }

  async function onFileSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0]
    if (!file) return
    try {
      setLoadingId('upload-file-btn')
      const result = await uploadProfileImage(
        file,
        user.id.value,
        globalUtils.functions.uuid()
      )
      form.setFieldValue(
        'processedImageUploadId',
        result.processedImageUploadId
      )
      previewUrl.value = result.processedImagePreviewUrl
    } finally {
      removeLoadingId('upload-file-btn')
    }
  }

  function showModal() {
    newCommandId()
    userId = globalUtils.functions.uuid()
    previewUrl.value = undefined
    form.reset(defaultValues)
    isOpen.value = true
  }

  defineExpose({ showModal })
</script>

<template>
  <modal v-model:open="isOpen" class="min-w-[400px]">
    <ModalHeader>
      <ModalHeaderTitle> Add Tenant User </ModalHeaderTitle>
      <ModalHeaderDescription>
        Create a new user in your tenant.
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalContent class="flex flex-col gap-5">
      <ModalSection>
        <div class="hidden" :data-testid="testIds.tenantUser.modal.add.userId">
          {{ userId }}
        </div>
        <FieldGroup>
          <FieldGroup row>
            <FormField
              :form="form"
              name="fullName"
              label="Full Name"
              class="flex-1"
              required
            >
              <template #default="{ field }">
                <InputGroupInput
                  :id="field.name"
                  :model-value="field.state.value ?? ''"
                  autofocus
                  :data-testid="testIds.tenantUser.modal.add.fullNameInput"
                  @update:model-value="field.handleChange"
                  @blur="field.handleBlur"
                />
              </template>
            </FormField>
            <FormField
              :form="form"
              name="initials"
              label="Initials"
              class="w-24"
              required
            >
              <template #default="{ field }">
                <InputGroupInput
                  :id="field.name"
                  :model-value="field.state.value ?? ''"
                  :max-length="3"
                  :data-testid="testIds.tenantUser.modal.add.initialsInput"
                  @update:model-value="field.handleChange"
                  @blur="field.handleBlur"
                />
              </template>
            </FormField>
          </FieldGroup>
          <FormField
            :form="form"
            name="email"
            label="Email"
            required
            :validators="{
              onChangeAsync: async ({ value }) => {
                if (!value || value.length < 3) return undefined
                if (!value.includes('@'))
                  return 'Email must contain an @ symbol'
                try {
                  const available =
                    await checkTenantUserEmailAvailabilityRequest(value)
                  return available ? undefined : 'Email is already in use'
                } catch (err) {
                  if ((err as Error).name === 'AbortError') return undefined
                  return 'Something went wrong.'
                }
              },
              onChangeAsyncDebounceMs: 400
            }"
          >
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                :data-testid="testIds.tenantUser.modal.add.emailInput"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
          <FormField :form="form" name="title" label="Job Title">
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                :data-testid="testIds.tenantUser.modal.add.titleInput"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
          <FormField
            :form="form"
            name="mobilePhoneNumber"
            label="Phone Number"
            :validators="phoneValidators"
          >
            <template #default="{ field }">
              <InputGroupPhone
                :id="field.name"
                :model-value="field.state.value ?? undefined"
                :data-testid="testIds.tenantUser.modal.add.mobilePhoneInput"
                @update:model-value="(v) => field.handleChange(v ?? '')"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
        </FieldGroup>
      </ModalSection>

      <ModalSection class="flex flex-col gap-5">
        <ActionCard @click="onUploadPhotoClicked">
          <Avatar v-if="previewUrl" class="size-10">
            <AvatarImage :src="previewUrl" />
            <AvatarFallback> </AvatarFallback>
          </Avatar>
          <ActionCardLeadIcon
            v-else
            :icon="IconUpload"
            :loading="isLoading('upload-file-btn')"
          />
          <div class="space-y-1">
            <ActionCardTitle>Upload Photo</ActionCardTitle>
            <ActionCardDescription>
              Choose a profile picture (PNG or JPG).
            </ActionCardDescription>
          </div>
        </ActionCard>
        <input
          ref="fileInput"
          type="file"
          accept="image/*"
          class="hidden"
          @change="onFileSelected"
        />
        <Popover v-model:open="activationPopoverOpen">
          <PopoverTrigger as-child>
            <ActionCard>
              <ActionCardLeadIcon :icon="IconCalendarClock" />
              <div class="space-y-1">
                <ActionCardTitle> Schedule User Activation </ActionCardTitle>
                <ActionCardDescription>
                  {{ activationDescription }}
                </ActionCardDescription>
              </div>
            </ActionCard>
          </PopoverTrigger>
          <PopoverContent
            class="w-auto p-0"
            align="start"
            :collision-padding="8"
            sticky="partial"
          >
            <DateCalendar
              :model-value="calendarValue"
              :disabled-ranges="[
                {
                  from: 'epoch',
                  to: Temporal.Now.plainDateISO().subtract({ days: 1 })
                }
              ]"
              @update:model-value="onActivationDateSelected"
            />
            <div
              v-if="activeFromValue"
              class="border-input flex justify-end border-t px-2 py-1.5"
            >
              <Button variant="ghost" size="sm" @click="clearActivationDate">
                Clear schedule
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </ModalSection>

      <ModalSection>
        <ModalSectionHeader>
          <ModalSectionHeaderTitle>Optional Settings</ModalSectionHeaderTitle>
        </ModalSectionHeader>
        <component :is="form.Field" name="grantTenantAdminPrivileges">
          <template #default="{ field }">
            <label
              class="bg-background shadow-xs ring-border relative flex cursor-pointer items-center gap-3.5 rounded-sm p-4 pr-16 ring-1 ring-inset"
              @click="
                () => {
                  field.handleChange(field.state.value !== true)
                  field.handleBlur()
                }
              "
            >
              <div
                class="bg-background shadow-xs ring-border flex size-10 shrink-0 items-center justify-center rounded-full ring-1 ring-inset"
              >
                <IconAdmin class="lucide-icon size-4 shrink-0" />
              </div>
              <Switch
                :id="field.name"
                :model-value="field.state.value === true"
                class="pointer-events-none absolute right-5 top-5"
                @update:model-value="
                  (v) => {
                    field.handleChange(v ?? false)
                    field.handleBlur()
                  }
                "
              />
              <div class="space-y-1">
                <div class="flex items-center gap-1 text-sm font-medium">
                  <span>Tenant Admin Privileges</span>
                </div>
                <div role="description" class="text-muted-foreground text-xs">
                  Gives the user full access to the tenant with all privileges
                  enabled.
                </div>
              </div>
            </label>
          </template>
        </component>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <FormSubmit
        :form="form"
        label="Add Tenant User"
        data-testid="add-tenant-user-ok-btn"
        :require-dirty="false"
      />
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </modal>
</template>
